import { useEffect, useState } from 'react';
import { listFleetNodes, type FleetNode } from '../../lib/fleetApi';
import {
  buildRoomsMap,
  waterfallHash,
  type Ek21MapRoom,
  type RoomsMapSection,
  type UtMapRoom,
} from './rooms-map';

const REFRESH_MS = 15_000;

const PLATFORM_LABEL: Record<RoomsMapSection['platform'], string> = {
  ut: 'UT',
  ek21: 'EK21',
  wootalk: 'WooTalk',
  site: 'site',
  jd: 'JD',
};

function sessionLink(sessionId: string | null) {
  if (sessionId === null) return null;
  return (
    <a className="text-xs underline" href={waterfallHash(sessionId)} data-testid="rooms-map-waterfall">
      看瀑布
    </a>
  );
}

function UtRooms({ rooms }: { rooms: UtMapRoom[] }) {
  if (rooms.length === 0) return <p className="text-sm text-text-tertiary">沒有房間列。</p>;
  return (
    <ul className="mt-2 flex flex-col gap-1">
      {rooms.map((room) => (
        <li key={room.id} className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1 text-sm" data-testid="rooms-map-ut-room">
          <span className="font-mono">{room.id}</span>
          <span>{room.name === '' ? '未命名' : room.name}</span>
          <span className="text-text-tertiary">線上 {room.online}</span>
          <span>{room.claimed ? '已佔' : '未佔'}</span>
          {sessionLink(room.sessionId)}
        </li>
      ))}
    </ul>
  );
}

function Ek21Rooms({ rooms }: { rooms: Ek21MapRoom[] }) {
  if (rooms.length === 0) return <p className="text-sm text-text-tertiary">沒有房間列。</p>;
  return (
    <ul className="mt-2 flex flex-col gap-1">
      {rooms.map((room) => (
        <li key={room.id} className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1 text-sm" data-testid="rooms-map-ek21-room">
          <span className="font-mono">{room.id}</span>
          <span className="text-text-tertiary">線上 {room.online}</span>
          <span className="text-text-tertiary">真人 {room.real}</span>
          {room.empty ? <span>空房</span> : null}
          {room.cooling ? <span>冷卻中</span> : null}
          {sessionLink(room.sessionId)}
        </li>
      ))}
    </ul>
  );
}

function SectionBody({ section }: { section: RoomsMapSection }) {
  if (section.read === 'missing') return <p className="text-sm text-text-tertiary">還沒有心跳。</p>;
  if (section.read === 'bad') return <p className="text-sm text-text-tertiary">無法讀取。</p>;
  switch (section.platform) {
    case 'ut':
      return (
        <>
          <p className="text-sm">
            已佔 {section.claimed}　未佔 {section.uncovered}
          </p>
          <UtRooms rooms={section.rooms} />
        </>
      );
    case 'ek21':
      return <Ek21Rooms rooms={section.rooms} />;
    case 'wootalk':
      return (
        <p className="text-sm">
          這是配對身體，沒有公開房間。配對 {section.matches}。{section.proxyDegraded ? '代理降級' : '代理正常'}。
        </p>
      );
    case 'site':
      return <p className="text-sm">{section.state === 'idle' ? '閒置' : section.state === 'up' ? '運作' : '狀態不明'}</p>;
    case 'jd':
      return (
        <p className="text-sm">
          {section.alert ? `告警${section.alertReason === '' ? '' : ` ${section.alertReason}`}` : '正常'}
        </p>
      );
    default: {
      const unreachable: never = section;
      return unreachable;
    }
  }
}

function PlatformSection({ section }: { section: RoomsMapSection }) {
  return (
    <section
      className="min-w-0 rounded border border-border p-3"
      data-testid={`rooms-map-${section.platform}`}
      data-read={section.read}
      data-stale={section.stale ? 'true' : 'false'}
    >
      <h2 className="flex flex-wrap items-baseline gap-2 text-sm font-medium">
        <span>{PLATFORM_LABEL[section.platform]}</span>
        {section.stale ? (
          <span className="inline-flex rounded-full bg-text-tertiary/15 px-2 py-0.5 text-[10px] text-danger">過期</span>
        ) : null}
        {section.nodeId ? <span className="font-mono text-[10px] text-text-tertiary">{section.nodeId}</span> : null}
      </h2>
      <div className="mt-2">
        <SectionBody section={section} />
      </div>
    </section>
  );
}

export function RoomsMapPage() {
  const [nodes, setNodes] = useState<FleetNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const next = await listFleetNodes();
        if (cancelled) return;
        setNodes(next);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    };
    void load();
    const id = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const sections = nodes === null ? null : buildRoomsMap(nodes);

  return (
    <div
      className="flex min-w-0 flex-col gap-4 p-3"
      data-testid="rooms-map-page"
      data-ready={sections === null ? 'false' : 'true'}
    >
      <header>
        <p className="text-[11px] uppercase tracking-widest text-text-tertiary">系統／佔用</p>
        <h1 className="font-serif text-xl" id="rooms-map-title">
          房間地圖
        </h1>
        <p className="mt-1 text-xs text-text-tertiary">
          佔用來自身體 heartbeat。協議進房仍走房間列表。
          <a className="ml-2 underline" href="#/system/sessions" data-testid="rooms-map-sessions">
            帳號列在工作階段
          </a>
        </p>
      </header>
      {error ? (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
      {sections === null && error === null ? (
        <p className="text-sm text-text-tertiary" role="status">
          載入中…
        </p>
      ) : null}
      {sections === null ? null : (
        <div className="flex min-w-0 flex-col gap-3" data-testid="rooms-map-occupancy">
          {sections.map((section) => (
            <PlatformSection key={section.platform} section={section} />
          ))}
        </div>
      )}
    </div>
  );
}
